import { useEffect, useRef, useState, type RefObject } from 'react';
import * as THREE from 'three';
import type { Decor, PlayerData } from '../../shared/types';
import { furnitureSize, getFurniture, text, type Locale } from '../../shared/content';
import { placementIssue } from '../../shared/room-rules';
import { createFurnitureModel } from './room-kit';
export default function RoomScene({
  decor,
  onSpot,
  captureRef,
  locale = 'en',
  home = 'starter',
  placing,
  layout,
}: {
  captureRef?: RefObject<(() => Promise<Blob | null>) | undefined>;
  decor: Decor[];
  onSpot?: (x: number, z: number) => void;
  locale?: Locale;
  home?: string;
  placing?: { itemId: string; rotation: number; x: number; z: number };
  layout?: Pick<PlayerData, 'home' | 'decor' | 'furnitureOwned'>;
}) {
  const host = useRef<HTMLDivElement>(null);
  const [failed, setFailed] = useState(false);
  const cb = useRef(onSpot);
  cb.current = onSpot;
  useEffect(() => {
    const el = host.current!;
    let renderer: THREE.WebGLRenderer;
    try {
      renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    } catch {
      setFailed(true);
      return;
    }
    renderer.setPixelRatio(Math.min(devicePixelRatio, 1.5));
    renderer.setClearColor('#e7dfcd');
    el.appendChild(renderer.domElement);
    const scene = new THREE.Scene();
    const camera = new THREE.OrthographicCamera(-6, 6, 5, -5, 0.1, 100);
    camera.position.set(10, 10, 12);
    camera.lookAt(0, 0, 0);
    scene.add(new THREE.HemisphereLight('#fff8e7', '#9e8b74', 3));
    const light = new THREE.DirectionalLight('#ffe7bd', 3);
    light.position.set(-3, 8, 5);
    scene.add(light);
    const box = (color: string, x: number, y: number, z: number, w: number, h: number, d: number) => {
      const m = new THREE.Mesh(
        new THREE.BoxGeometry(w, h, d),
        new THREE.MeshStandardMaterial({ color, roughness: 1 }),
      );
      m.position.set(x, y, z);
      scene.add(m);
      return m;
    };
    box('#b99771', 0, -0.16, 0, 8, 0.3, 8);
    box(home === 'loft' ? '#e2b99b' : '#eee0c6', -4, 1.4, 0, 0.15, 3, 8);
    box(home === 'studio' ? '#d3dfd3' : '#e5d6ba', 0, 1.4, -4, 8, 3, 0.15);
    for (let x = -3.5; x < 4; x++) box('#ae8b65', x, 0.005, 0, 0.04, 0.015, 8);
    box('#8fa6a0', -1.6, 1.7, -3.88, 2.2, 1.6, 0.05);
    box('#f6eaca', -1.6, 1.7, -3.8, 0.06, 1.65, 0.08);
    box('#f6eaca', -1.6, 1.7, -3.8, 2.25, 0.06, 0.08);
    if (home !== 'starter') {
      box('#9bb8b3', 1.6, 1.7, -3.88, 2.2, 1.6, 0.05);
      box('#f6eaca', 1.6, 1.7, -3.8, 0.06, 1.65, 0.08);
      box('#f6eaca', 1.6, 1.7, -3.8, 2.25, 0.06, 0.08);
    }
    box('#b58b64', -2.8, 0.25, -2.3, 1.5, 0.5, 2.2);
    box('#f2e7d4', -2.8, 0.58, -2.3, 1.5, 0.2, 2.2);
    box('#70918c', -2.8, 0.72, -1.9, 1.5, 0.12, 1.3);
    box('#fff5e6', -2.8, 0.76, -3, 1.2, 0.15, 0.5);
    for (const d of decor) {
      if (!getFurniture(d.itemId) || d.itemId === placing?.itemId) continue;
      const model = createFurnitureModel(d.itemId);
      model.position.set(d.x, 0, d.z);
      model.rotation.y = (d.rotation * Math.PI) / 2;
      scene.add(model);
    }
    let ghost: THREE.Group | undefined, footprint: THREE.Mesh | undefined;
    const preview = (x: number, z: number) => {
      if (!ghost || !footprint || !placing) return;
      ghost.position.set(x, 0.06, z);
      footprint.position.set(x, 0.06, z);
      const invalid = layout && placementIssue(layout, placing.itemId, x, z, placing.rotation);
      (footprint.material as THREE.MeshBasicMaterial).color.set(invalid ? '#b55d4b' : '#427e6b');
    };
    if (placing && getFurniture(placing.itemId)) {
      ghost = createFurnitureModel(placing.itemId);
      ghost.rotation.y = (placing.rotation * Math.PI) / 2;
      ghost.traverse((o) => {
        if (o instanceof THREE.Mesh) {
          const m = o.material as THREE.MeshStandardMaterial;
          m.transparent = true;
          m.opacity = 0.62;
        }
      });
      scene.add(ghost);
      const size = furnitureSize(getFurniture(placing.itemId)!.kind, placing.rotation);
      footprint = new THREE.Mesh(
        new THREE.PlaneGeometry(size.width + 0.12, size.depth + 0.12),
        new THREE.MeshBasicMaterial({ transparent: true, opacity: 0.45, side: THREE.DoubleSide }),
      );
      footprint.rotation.x = -Math.PI / 2;
      scene.add(footprint);
      const grid = new THREE.GridHelper(8, 8, '#9a866a', '#bca88c');
      grid.position.y = 0.045;
      scene.add(grid);
      preview(placing.x, placing.z);
    }
    const resize = () => {
      const w = el.clientWidth,
        h = el.clientHeight;
      renderer.setSize(w, h);
      camera.left = (-5.5 * w) / h;
      camera.right = (5.5 * w) / h;
      camera.updateProjectionMatrix();
      renderer.render(scene, camera);
    };
    if (captureRef)
      captureRef.current = () => {
        renderer.render(scene, camera);
        const originalWidth = el.clientWidth,
          originalHeight = el.clientHeight;
        renderer.setSize(originalWidth * 3, originalHeight * 3, false);
        renderer.render(scene, camera);
        return new Promise((resolve) =>
          renderer.domElement.toBlob((blob) => {
            renderer.setSize(originalWidth, originalHeight);
            renderer.render(scene, camera);
            resolve(blob);
          }, 'image/png'),
        );
      };
    const ro = new ResizeObserver(resize);
    ro.observe(el);
    resize();
    const point = (e: PointerEvent) => {
      const r = el.getBoundingClientRect();
      const ray = new THREE.Raycaster();
      ray.setFromCamera(
        new THREE.Vector2(
          ((e.clientX - r.left) / r.width) * 2 - 1,
          (-(e.clientY - r.top) / r.height) * 2 + 1,
        ),
        camera,
      );
      const p = ray.ray.intersectPlane(new THREE.Plane(new THREE.Vector3(0, 1, 0), 0), new THREE.Vector3());
      return p && Math.abs(p.x) <= 3.5 && Math.abs(p.z) <= 3.5
        ? { x: Math.max(-3, Math.min(3, Math.round(p.x))), z: Math.max(-3, Math.min(3, Math.round(p.z))) }
        : null;
    };
    const move = (e: PointerEvent) => {
      const p = point(e);
      if (p) {
        preview(p.x, p.z);
        renderer.render(scene, camera);
      }
    };
    const click = (e: PointerEvent) => {
      const p = point(e);
      if (p) cb.current?.(p.x, p.z);
    };
    el.addEventListener('pointerup', click);
    el.addEventListener('pointermove', move);
    return () => {
      if (captureRef) captureRef.current = undefined;
      ro.disconnect();
      el.removeEventListener('pointerup', click);
      el.removeEventListener('pointermove', move);
      scene.traverse((o) => {
        if (o instanceof THREE.Mesh || o instanceof THREE.LineSegments) {
          o.geometry.dispose();
          (o.material as THREE.Material).dispose();
        }
      });
      renderer.dispose();
      renderer.domElement.remove();
    };
  }, [decor, home, placing?.itemId, placing?.rotation, placing?.x, placing?.z]);
  return (
    <div
      className={`room-scene ${onSpot ? 'placing' : ''}`}
      ref={host}
      aria-label={text('Your 3D room', '你的3D房间', '你的3D房間', 'Tu habitación 3D')[locale]}
    >
      {failed && (
        <p className="scene-fallback">
          {
            text(
              '3D unavailable. Use the position controls below.',
              '3D 暂不可用，请使用下方的位置选项。',
              '3D 暫不可用，請使用下方的位置選項。',
              '3D no disponible. Usa los controles de posición.',
            )[locale]
          }
        </p>
      )}
    </div>
  );
}
